import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAllFootprintsForAdmin,
  hideFootprint,
  deleteFootprint,
  fetchDailyLimit,
  updateDailyLimit,
} from '../../services/footprintService';
import { adminSignOut } from '../../services/adminAuthService';

// TTL定数（useFootprints.jsと同じ値）
const NORMAL_TTL_MS = 48 * 60 * 60 * 1000;
const SUPER_TTL_MS  = 72 * 60 * 60 * 1000;

/** 残り時間をわかりやすい文字列に変換 */
const formatRemaining = (post) => {
  const ttl = post.isSuper ? SUPER_TTL_MS : NORMAL_TTL_MS;
  const remMs = new Date(post.createdAt).getTime() + ttl - Date.now();
  if (remMs <= 0) return '期限切れ';
  const h = Math.floor(remMs / (60 * 60 * 1000));
  const m = Math.floor((remMs % (60 * 60 * 1000)) / (60 * 1000));
  return `残り ${h}時間${m}分`;
};

/** 残り時間に応じた色クラス */
const remainingColor = (post) => {
  const ttl = post.isSuper ? SUPER_TTL_MS : NORMAL_TTL_MS;
  const remMs = new Date(post.createdAt).getTime() + ttl - Date.now();
  if (remMs <= 0) return 'text-gray-400';
  if (remMs < 12 * 60 * 60 * 1000) return 'text-red-500';
  if (remMs < 24 * 60 * 60 * 1000) return 'text-yellow-500';
  return 'text-blue-500';
};

const AdminPostsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyLimit, setDailyLimit] = useState(null);
  const [limitInput, setLimitInput] = useState('');
  // ページネーション
  const [cursorStack, setCursorStack] = useState([null]); // [null, cursor1, cursor2, ...]
  const [currentPage, setCurrentPage] = useState(0);      // cursorStack のインデックス
  const [hasMore, setHasMore] = useState(false);
  // 拡大表示用
  const [zoomUrl, setZoomUrl] = useState(null);

  /** 指定カーソルでページ取得 */
  const loadPage = useCallback(async (cursor) => {
    setLoading(true);
    try {
      const { posts: data, hasMore: more, nextCursor } = await fetchAllFootprintsForAdmin({ cursor });
      setPosts(data);
      setHasMore(more);
      return nextCursor;
    } catch (err) {
      console.error('投稿取得失敗:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 初期ロード
  useEffect(() => {
    const init = async () => {
      try {
        const [limitData] = await Promise.all([fetchDailyLimit()]);
        setDailyLimit(limitData);
        setLimitInput(String(limitData));
      } catch (err) {
        console.error('設定取得失敗:', err);
      }
      await loadPage(null);
    };
    init();
  }, [loadPage]);

  /** 次のページへ */
  const handleNext = async () => {
    const nextCursor = await loadPage(cursorStack[currentPage + 1] ?? null);
    // まだスタックにない場合は追加
    if (cursorStack.length <= currentPage + 1 && nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
    setCurrentPage((p) => p + 1);
  };

  /** 前のページへ */
  const handlePrev = async () => {
    const prevPage = currentPage - 1;
    await loadPage(cursorStack[prevPage]);
    setCurrentPage(prevPage);
  };

  const handleToggleHide = async (postId, currentIsVisible) => {
    const nextVisible = !currentIsVisible;
    try {
      const result = await hideFootprint(postId, nextVisible);
      if (!result.success) throw new Error(result.message ?? '操作失敗');
      // リストから消さず isVisible 状態だけ更新する（再表示操作のために残す）
      setPosts((prev) =>
        prev.map((p) => (p.postId === postId ? { ...p, isVisible: nextVisible } : p))
      );
    } catch (err) {
      console.error('表示切替失敗:', err);
      alert('操作に失敗しました');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('この投稿を削除しますか？')) return;
    try {
      const result = await deleteFootprint(postId);
      if (!result.success) throw new Error(result.message ?? '削除失敗');
      setPosts((prev) => prev.filter((p) => p.postId !== postId));
    } catch (err) {
      alert('削除に失敗しました');
    }
  };

  const handleUpdateLimit = async () => {
    const newLimit = parseInt(limitInput, 10);
    if (isNaN(newLimit) || newLimit < 1) {
      alert('1以上の整数を入力してください');
      return;
    }
    try {
      await updateDailyLimit(newLimit);
      setDailyLimit(newLimit);
      alert(`投稿上限を ${newLimit} 件に更新しました`);
    } catch (err) {
      alert('更新に失敗しました');
    }
  };

  const handleSignOut = async () => {
    await adminSignOut();
    navigate('/admin', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-700">足あと管理</h1>
        <button onClick={handleSignOut} className="text-sm text-gray-500 underline">
          ログアウト
        </button>
      </div>

      {/* 1日投稿上限設定 */}
      <div className="bg-white rounded-xl shadow p-4 mb-6 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-600">1日の投稿上限：</span>
        <input
          type="number"
          min="1"
          value={limitInput}
          onChange={(e) => setLimitInput(e.target.value)}
          className="border rounded px-2 py-1 w-20 text-sm"
        />
        <span className="text-sm text-gray-400">（現在: {dailyLimit ?? '—'} 件）</span>
        <button
          onClick={handleUpdateLimit}
          className="bg-blue-500 text-white text-sm px-3 py-1 rounded-lg hover:bg-blue-600"
        >
          更新
        </button>
      </div>

      {/* 投稿一覧 */}
      {loading ? (
        <p className="text-center text-gray-400 text-sm mt-10">読み込み中...</p>
      ) : posts.length === 0 ? (
        <p className="text-center text-gray-400 text-sm mt-10">投稿がありません</p>
      ) : (
        <div className="flex flex-col gap-3">
          {posts.map((post) => (
            <div key={post.postId} className={`bg-white rounded-xl shadow p-4 ${!post.isVisible ? 'opacity-50' : ''}`}>
              {/* 画像エリア（写真＋サイン横並び） */}
              {(post.photoUrl || post.handwritingUrl) && (
                <div className="flex gap-2 mb-3">
                  {post.photoUrl && (
                    <button
                      onClick={() => setZoomUrl(post.photoUrl)}
                      className="flex-1 aspect-square max-w-[120px]"
                    >
                      <img
                        src={post.photoUrl}
                        alt="写真"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </button>
                  )}
                  {post.handwritingUrl && (
                    <button
                      onClick={() => setZoomUrl(post.handwritingUrl)}
                      className="flex-1 aspect-square max-w-[120px] bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <img
                        src={post.handwritingUrl}
                        alt="サイン"
                        className="w-full h-full object-contain rounded-lg p-1"
                      />
                    </button>
                  )}
                </div>
              )}

              {/* テキスト情報 + ボタン */}
              <div className="flex gap-3 items-start">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-700 truncate">{post.nickname}</p>
                  {post.isSuper && (
                    <span className="text-xs text-yellow-500 font-bold">★すっごーい！足あと</span>
                  )}
                  {/* 投稿時間 */}
                  <p className="text-xs text-gray-400 mt-1">
                    投稿: {post.createdAt ? new Date(post.createdAt).toLocaleString('ja-JP') : '—'}
                  </p>
                  {/* 残り表示時間 */}
                  {post.createdAt && (
                    <p className={`text-xs font-medium mt-0.5 ${remainingColor(post)}`}>
                      {formatRemaining(post)}
                      {post.isSuper && <span className="text-gray-400 font-normal">（72h）</span>}
                    </p>
                  )}
                  {/* 住所 */}
                  {post.address && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{post.address}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleHide(post.postId, post.isVisible)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 rounded px-2 py-1"
                  >
                    {post.isVisible ? '非表示' : '表示'}
                  </button>
                  <button
                    onClick={() => handleDelete(post.postId)}
                    className="text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded px-2 py-1"
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ページネーション */}
      {!loading && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={handlePrev}
            disabled={currentPage === 0}
            className="text-sm px-4 py-2 rounded-lg bg-white shadow disabled:opacity-30"
          >
            ← 前へ
          </button>
          <span className="text-sm text-gray-500">{currentPage + 1} ページ目</span>
          <button
            onClick={handleNext}
            disabled={!hasMore}
            className="text-sm px-4 py-2 rounded-lg bg-white shadow disabled:opacity-30"
          >
            次へ →
          </button>
        </div>
      )}

      {/* 拡大モーダル */}
      {zoomUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomUrl(null)}
        >
          <img
            src={zoomUrl}
            alt="拡大"
            className="max-w-full max-h-full rounded-xl object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default AdminPostsPage;